import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  HOUR_HEIGHT,
  appointmentBlock,
  blockStyle,
  blockedBlock,
  dayBounds,
  heightFor,
  hourMarks,
  layoutColumn,
  offsetFor,
  titleStyle,
  clientLabel,
} from "./diaryShared";
import { AppointmentHoverCard, StatusBadge, blockLabel, useHoverCard } from "./DiaryStatus";
import { formatDate, formatTime, inZone, todayIn } from "../../services/datetime";

/**
 * The week view: seven day columns, everybody's work together.
 *
 * Deliberately not split by provider — that is the day view's job. A week is
 * for reading load and spotting the quiet afternoon, so the whole branch shares
 * a column per day and blocks stack side by side where they overlap.
 */
export default function DiaryWeekView({
  days,
  timezone,
  appointments,
  blocks,
  workingHours,
  onSelect,
  onEmptyClick,
}) {
  const { t, i18n } = useTranslation();
  const hover = useHoverCard();

  const bounds = useMemo(
    () => dayBounds(days, workingHours, appointments, blocks, timezone),
    [days, workingHours, appointments, blocks, timezone]
  );

  const marks = useMemo(() => hourMarks(bounds), [bounds]);
  const gridHeight = ((bounds.end - bounds.start) / 60) * HOUR_HEIGHT;
  const today = todayIn(timezone);

  /** Positioned blocks per day, already laid out side by side. */
  const byDay = useMemo(() => {
    const buckets = Object.fromEntries(days.map((d) => [d, []]));

    days.forEach((date) => {
      appointments.forEach((appointment) => {
        const block = appointmentBlock(appointment, date, timezone);
        if (block) buckets[date].push(block);
      });

      blocks.forEach((blocked) => {
        const block = blockedBlock(blocked, date, timezone);
        if (block) buckets[date].push(block);
      });
    });

    // Cancelled and no-show make room for live work rather than covering it.
    return Object.fromEntries(Object.entries(buckets).map(([d, items]) => [d, layoutColumn(items)]));
  }, [days, appointments, blocks, timezone]);

  const nowMinutes = (() => {
    const now = inZone(new Date().toISOString(), timezone);
    return days.includes(now.date) && now.minutes >= bounds.start && now.minutes <= bounds.end
      ? { date: now.date, minutes: now.minutes }
      : null;
  })();

  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <div className="min-w-[720px]">
          {/* Day headings */}
          <div className="flex border-b border-line bg-raised">
            <div className="w-14 flex-shrink-0" />
            {days.map((date) => (
              <div
                key={date}
                className={`flex-1 border-s border-line px-1 py-2 text-center ${
                  date === today ? "bg-accent-wash" : ""
                }`}
              >
                <p className="text-xs font-medium text-ink">
                  {formatDate(date, i18n.language, { weekday: "short", year: undefined })}
                </p>
              </div>
            ))}
          </div>

          <div className="relative flex" style={{ height: gridHeight }}>
            {/* Hour axis */}
            <div className="relative w-14 flex-shrink-0">
              {marks.map((mark) => (
                <div
                  key={mark.minutes}
                  className="absolute -translate-y-1/2 pe-2 text-end text-caption text-ink-2"
                  dir="ltr"
                  style={{ top: offsetFor(mark.minutes, bounds.start), width: "100%" }}
                >
                  {mark.label}
                </div>
              ))}
            </div>

            {days.map((date) => (
              <div
                key={date}
                className={`relative flex-1 border-s border-line ${
                  date === today ? "bg-accent-wash" : ""
                }`}
                onDoubleClick={() => onEmptyClick?.({ date })}
              >
                {marks.map((mark) => (
                  <div
                    key={mark.minutes}
                    className="absolute w-full border-t border-line"
                    style={{ top: offsetFor(mark.minutes, bounds.start) }}
                  />
                ))}

                {(byDay[date] ?? []).map((block) => {
                  const width = `${block.width}%`;
                  const inset = `${block.left}%`;
                  const height = heightFor(block.span.to - block.span.from);
                  const top = offsetFor(block.span.from, bounds.start);

                  if (block.kind === "blocked") {
                    return (
                      <div
                        key={`b-${block.id}-${date}`}
                        title={block.blocked.reason || t("diary.blocked")}
                        className="absolute overflow-hidden rounded border border-line-strong bg-raised"
                        style={{ top, height, width, insetInlineStart: inset }}
                      />
                    );
                  }

                  const a = block.appointment;
                  const roomy = height >= 34 && !block.ghost;

                  return (
                    <button
                      key={`a-${block.id}-${date}`}
                      type="button"
                      aria-label={blockLabel(t, a)}
                      {...hover.bind(a)}
                      onClick={() => onSelect?.(a)}
                      className={`absolute z-10 overflow-hidden rounded px-1 py-0.5 text-start text-caption leading-tight shadow-e1 transition-shadow hover:shadow-e2 focus-visible:ring-2 ${blockStyle(a.status)}`}
                      style={{ top, height, width, insetInlineStart: inset }}
                    >
                      {/* A seventh of the width: the swatch carries the status
                          and the word lives in the hover card. A short block
                          folds the time into its one line. */}
                      <span className="flex min-w-0 items-center gap-1">
                        <StatusBadge status={a.status} dotOnly />
                        {!roomy && (
                          <span className="flex-shrink-0 tabular-nums" dir="ltr">
                            {formatTime(a.local_starts_at)}
                          </span>
                        )}
                        <span className={`truncate font-medium ${titleStyle(a.status)}`}>
                          {clientLabel(t, a)}
                        </span>
                      </span>
                      {roomy && (
                        <span className="block truncate tabular-nums opacity-80" dir="ltr">
                          {formatTime(a.local_starts_at)}
                        </span>
                      )}
                    </button>
                  );
                })}

                {nowMinutes?.date === date && (
                  <div
                    className="pointer-events-none absolute z-20 flex w-full items-center"
                    style={{ top: offsetFor(nowMinutes.minutes, bounds.start) }}
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-danger" />
                    <span className="h-px flex-1 bg-danger" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <AppointmentHoverCard card={hover.card} />
    </div>
  );
}
